import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Divider, FormControlLabel, Paper, Switch, TextField, Typography } from '@mui/material';
import { ApprovalControlService } from '../../services/approvalControl.service';
import { ApprovalDocumentType, IApprovalControl } from '../../interfaces/approvalControl.interface';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';

const DOCS: Record<ApprovalDocumentType, { label: string; amount1: string; amount2?: string; help: string }> = {
    STOCKTAKE: {
        label: 'Stocktake',
        amount1: 'Creator may approve below BDT (total adjustment)',
        amount2: 'Creator may approve below BDT (largest line)',
        help: 'The person who created the count may approve it only when the total and the largest line are both under these amounts.',
    },
    PURCHASE_ORDER: {
        label: 'Purchase order',
        amount1: 'Creator may approve below BDT',
        help: 'Applies to orders without approval bands. Orders with bands keep their band rules.',
    },
    REQUISITION: {
        label: 'Requisition',
        amount1: 'Creator may approve below BDT',
        help: 'The value is an estimate: approved quantity at the sending store’s average stock cost.',
    },
    STOCK_TRANSFER: {
        label: 'Stock transfer',
        amount1: 'Creator may approve below BDT',
        help: 'The value is an estimate: quantity at the sending store’s average stock cost.',
    },
};

/**
 * "Approval controls" card for the institution settings page. Per document type: whether a second person must
 * approve, and below which amount the creator may still approve their own document. Hidden if the read fails.
 */
const ApprovalControlsCard: React.FC<{ institutionId: string }> = ({ institutionId }) => {
    const toast = useToast();
    const canManage = usePermission(PERMISSIONS.INSTITUTION_SETTINGS_MANAGE);
    const [rows, setRows] = useState<IApprovalControl[] | null>(null);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try {
            setRows((await ApprovalControlService.get(institutionId)).controls);
        } catch {
            setRows(null);
        }
    }, [institutionId]);

    useEffect(() => {
        load();
    }, [load]);

    if (!rows) return null;

    const patch = (type: ApprovalDocumentType, change: Partial<IApprovalControl>) =>
        setRows(rows.map(r => (r.documentType === type ? { ...r, ...change } : r)));

    const num = (v: string) => (v === '' ? 0 : Number(v));

    const save = async () => {
        try {
            setSaving(true);
            setRows((await ApprovalControlService.save(institutionId, { controls: rows })).controls);
            toast.showSuccess('Approval controls saved');
        } catch (error) {
            toast.showApiError(error, 'Failed to save approval controls');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Paper sx={{ p: 3, mt: 3 }} data-help-section="institutions.approvalControls.section">
            <Typography variant="h6" sx={{ mb: 2 }}>Approval controls</Typography>
            <Divider sx={{ mb: 2 }} />

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Switch on &quot;Needs a second person&quot; so nobody approves a document they created. You can still let the
                creator approve small documents by setting an amount: below it, the creator may approve and the document is
                marked Self-approved. Someone other than the creator can always approve.
            </Typography>

            {rows.map(row => {
                const meta = DOCS[row.documentType];
                if (!meta) return null;
                return (
                    <Box key={row.documentType} sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                            <Box sx={{ flex: '1 1 220px', maxWidth: 280 }}>
                                <Typography variant="subtitle2">{meta.label}</Typography>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={row.enabled}
                                            disabled={saving || !canManage}
                                            onChange={(e) => patch(row.documentType, { enabled: e.target.checked })}
                                            inputProps={{ 'aria-label': `${meta.label} needs a second person` }}
                                        />
                                    }
                                    label="Needs a second person"
                                />
                            </Box>
                            <Box sx={{ flex: '1 1 220px', maxWidth: 300 }}>
                                <TextField
                                    size="small" type="number" label={meta.amount1}
                                    value={row.amount1}
                                    disabled={saving || !canManage || !row.enabled}
                                    onChange={(e) => patch(row.documentType, { amount1: num(e.target.value) })}
                                    inputProps={{ min: 0 }}
                                    sx={{ width: '100%' }}
                                />
                            </Box>
                            {meta.amount2 && (
                                <Box sx={{ flex: '1 1 220px', maxWidth: 300 }}>
                                    <TextField
                                        size="small" type="number" label={meta.amount2}
                                        value={row.amount2 ?? 0}
                                        disabled={saving || !canManage || !row.enabled}
                                        onChange={(e) => patch(row.documentType, { amount2: num(e.target.value) })}
                                        inputProps={{ min: 0 }}
                                        sx={{ width: '100%' }}
                                    />
                                </Box>
                            )}
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {meta.help}
                        </Typography>
                    </Box>
                );
            })}

            <Typography variant="body2" sx={{ mt: 1, mb: 2 }}>
                Supplier bill hold release always needs a second person.
            </Typography>

            <Button variant="contained" onClick={save} disabled={saving || !canManage}>Save approval controls</Button>
            {!canManage && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Only an administrator who can manage institution settings can change this.
                </Typography>
            )}
        </Paper>
    );
};

export default ApprovalControlsCard;
